// src/features/tasks/pages/EditTaskPage.jsx
import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { addDays, subDays, addHours, startOfDay, format, isSameDay } from 'date-fns';

// Components
import TaskForm from '../components/TaskForm';

// Hooks
import { useTasks } from '../hooks/useTasks';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useTranslation } from 'react-i18next';
import { FormPage, FormActions } from '../../../components/common/form';

export default function EditTaskPage() {
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { t } = useTranslation(["common", "tasks"])
  const { taskId } = useParams();
  const { showSuccess, showError, showInfo } = useSnackbar();

  // State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    procedure_id: null,
    priority: 'MEDIUM',
    scheduled_date: new Date(),
    due_date: addHours(new Date(), 48), // Default to 48 hours from now
    becomes_public_at: startOfDay(new Date()),
    assignment_mode: 'simple',
    primary_assignee_id: null,
    secondary_assignee_id: null,
    weekday_assignments: null,
    is_recurring: false,
    rrule: null,
    skip_closing_days: false,
    requires_dual_signature: false,
    mark_not_completed_when_overdue: false,
    quick_complete: true,
    send_email: false,
    email_recipient: '',
    email_subject: '',
    email_body: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  useEffect(() => {
    console.log("FORM UPDATED", formData)
  }, [formData]);

  // API hooks
  const {
    currentTask,
    updateTask,
    fetchTaskById,
    loading,
    error,
    clearError,
  } = useTasks();

  // Load task data when component mounts
  useEffect(() => {
    if (taskId && !currentTask) {
      loadTaskData();
    }
  }, [taskId]);

  // Update form data when task is loaded
  useEffect(() => {
    if (currentTask && !initialDataLoaded) {
      console.log("CURRENT TASK", currentTask, {
        title: currentTask.title || '',
        description: currentTask.description || '',
        category_id: currentTask.category_id || '',
        procedure_id: currentTask.procedure_id || null,
        scheduled_date: new Date(currentTask.scheduled_date) || '',
        due_date: new Date(currentTask.due_date) || '',
        becomes_public_at: new Date(currentTask.becomes_public_at) || '',
        priority: currentTask.priority || 'MEDIUM',
        primary_assignee_id: currentTask.primary_assignee_id || null,
        secondary_assignee_id: currentTask.secondary_assignee_id || null,
        is_recurring: currentTask.is_recurring || false,
        rrule: currentTask.rrule || null,
      })
      setFormData({
        title: currentTask.title || '',
        description: currentTask.description || '',
        category_id: currentTask.category_id || '',
        procedure_id: currentTask.procedure_id || null,
        scheduled_date: new Date(currentTask.scheduled_date) || '',
        due_date: new Date(currentTask.due_date) || '',
        becomes_public_at: new Date(currentTask.becomes_public_at) || '',
        priority: currentTask.priority || 'MEDIUM',
        assignment_mode: currentTask.assignment_mode || 'simple',
        primary_assignee_id: currentTask.primary_assignee_id || null,
        secondary_assignee_id: currentTask.secondary_assignee_id || null,
        weekday_assignments: currentTask.weekday_assignments || null,
        is_recurring: currentTask.is_recurring || false,
        rrule: currentTask.rrule || null,
        skip_closing_days: currentTask.skip_closing_days || false,
        requires_dual_signature: currentTask.requires_dual_signature || false,
        mark_not_completed_when_overdue: currentTask.mark_not_completed_when_overdue || false,
        quick_complete: currentTask.quick_complete ?? true,
        send_email: currentTask.send_email || false,
        email_recipient: currentTask.email_recipient || '',
        email_subject: currentTask.email_subject || '',
        email_body: currentTask.email_body || '',
      });
      setInitialDataLoaded(true);
    }
  }, [currentTask, initialDataLoaded]);

  const loadTaskData = useCallback(async () => {
    try {
      await fetchTaskById(taskId);
    } catch (error) {
      showError(t('tasks:errors.load_failed'));
    }
  }, [taskId, fetchTaskById, showError]);

  // Form handling
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation error for this field
    setValidationErrors(prev => {
      if (prev[field]) {
        const { [field]: removed, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  }, []);

  const validateForm = useCallback(() => {
    const errors = {};

    // Title validation
    if (!formData.title?.trim()) {
      errors.title = t('tasks:validation.title_required');
    } else if (formData.title.length > 255) {
      errors.title = t('tasks:validation.title_too_long');
    }

    // Category validation
    if (!formData.category_id) {
      errors.category_id = t('tasks:validation.category_required');
    }

    // Description length validation
    if (formData.description && formData.description.length > 2000) {
      errors.description = t('tasks:validation.description_too_long');
    }

    // Assignee validation - ensure they're different if both are selected
    if (formData.primary_assignee_id === formData.secondary_assignee_id && formData.primary_assignee_id) {
      errors.secondary_assignee_id = t('tasks:validation.secondary_same_as_primary');
    }

    // Recurring task validation
    if (formData.is_recurring) {
      if (!formData.rrule) {
        errors.rrule = t('tasks:validation.recurrence_required');
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, t]);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare update data - only send fields that have changed
      const updateData = {};
      const currentData = {
        title: currentTask.title || '',
        description: currentTask.description || '',
        category_id: currentTask.category_id || '',
        procedure_id: currentTask.procedure_id || null,
        scheduled_date: new Date(currentTask.scheduled_date) || '',
        due_date: new Date(currentTask.due_date) || '',
        becomes_public_at: new Date(currentTask.becomes_public_at) || '',
        priority: currentTask.priority || 'MEDIUM',
        assignment_mode: currentTask.assignment_mode || 'simple',
        primary_assignee_id: currentTask.primary_assignee_id || null,
        secondary_assignee_id: currentTask.secondary_assignee_id || null,
        weekday_assignments: currentTask.weekday_assignments || null,
        is_recurring: currentTask.is_recurring || false,
        rrule: currentTask.rrule || null,
        skip_closing_days: currentTask.skip_closing_days || false,
        requires_dual_signature: currentTask.requires_dual_signature || false,
        mark_not_completed_when_overdue: currentTask.mark_not_completed_when_overdue || false,
        quick_complete: currentTask.quick_complete ?? true,
      };
      console.log("SUBMITTING", currentData, formData)

      // Compare and add only changed fields
      Object.keys(formData).forEach(key => {
        let hasChanged = false;

        // Handle specific date fields using isSameDay
        if (['scheduled_date', 'due_date', 'becomes_public_at'].includes(key)) {
          hasChanged = !isSameDay(formData[key], currentData[key]);
        } else if (key === 'weekday_assignments') {
          // Deep comparison for weekday_assignments object
          hasChanged = JSON.stringify(formData[key]) !== JSON.stringify(currentData[key]);
        } else {
          // For non-date fields, use regular comparison
          hasChanged = formData[key] !== currentData[key];
        }

        if (hasChanged) {
          updateData[key] = formData[key];
        }
      });

      console.log("UPDATED", updateData)

      // If no changes, show info message
      if (Object.keys(updateData).length === 0) {
        showInfo(t('tasks:success.no_changes'));
        navigate(`/tasks/${taskId}`);
        return;
      }

      // Format dates for API if they were changed
      if (updateData.scheduled_date) {
        updateData.scheduled_date = updateData.scheduled_date.toISOString().split('T')[0];
      }
      if (updateData.due_date) {
        updateData.due_date = updateData.due_date.toISOString().split('T')[0];
      }
      if (updateData.becomes_public_at) {
        updateData.becomes_public_at = updateData.becomes_public_at.toISOString().split('T')[0];
      }

      const result = await updateTask(taskId, updateData);

      if (result.success) {
        showSuccess(t('tasks:success.task_updated'));
        navigate(`/tasks/${taskId}`);
      } else {
        showError(result.error || 'Failed to update task');
      }
    } catch (error) {
      showError(t('tasks:errors.unexpected'));
    }
  }, [validateForm, formData, currentTask, taskId, updateTask, showSuccess, showError, navigate]);

  const handleCancel = useCallback(() => {
    navigateBack(`/tasks/${taskId}`);
  }, [navigateBack, taskId]);

  // Loading state
  if (loading && !currentTask) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={48} />
      </Box>
    );
  }

  // Task not found
  if (!currentTask && !loading) {
    return (
      <FormPage title={t("tasks:pages.edit_task")} onBack={() => navigateBack('/tasks')}>
        <Alert severity="error">
          {t("tasks:pages.task_not_found")}
        </Alert>
      </FormPage>
    );
  }

  return (
    <FormPage
      title={t("tasks:pages.edit_task")}
      subtitle={currentTask ? `${t("tasks:pages.editing")} ${currentTask.title}` : t("tasks:pages.edit_subtitle")}
      crumb={currentTask?.title}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={validationErrors}
      error={error}
      onErrorClose={clearError}
      maxWidth={960}
    >
      {initialDataLoaded && (
        <TaskForm
          formData={formData}
          onInputChange={handleInputChange}
          validationErrors={validationErrors}
          loading={loading}
          mode="edit"
        />
      )}
      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={loading ? t("tasks:pages.updating") : t("tasks:pages.update_task")}
        submitIcon={<SaveIcon />}
        loading={loading}
        disabled={!initialDataLoaded}
      />
    </FormPage>
  );
}
